import { useEffect, useState } from 'react';

import { LookupAutocompleteView } from '@/shared/ui/molecules/LookupAutocomplete';

import { useAuthorName } from '../../../hooks/useAuthorName';
import { useAuthorSearchDropdown } from '../../../hooks/useAuthorSearchDropdown';

export interface AuthorAutocompleteProps {
  /** ID do autor selecionado como string (`''` quando nada). */
  readonly value: string;
  /** Notifica o novo ID e rótulo (`''` ao limpar). */
  readonly onChange: (id: string, label: string) => void;
  readonly label: string;
  readonly placeholder: string;
  readonly hasError?: boolean;
}

/**
 * Autocomplete de autor (lookup `exam/author-dropdown`) com busca server-side.
 * Wrapper fino sobre o {@link LookupAutocompleteView} compartilhado.
 *
 * Ao editar, a questão traz apenas o `personAuthorId`; o nome do autor já
 * selecionado é resolvido via {@link useAuthorName} para pré-preencher o campo.
 */
export function AuthorAutocomplete({
  value,
  onChange,
  label,
  placeholder,
  hasError,
}: AuthorAutocompleteProps) {
  const [search, setSearch] = useState('');
  const [inputText, setInputText] = useState('');
  const { options, isQuerySuccess } = useAuthorSearchDropdown(search);
  const resolvedLabel = useAuthorName(value);

  // Preenche o rótulo exibido quando o nome do autor inicial é resolvido pelo id.
  // Só sincroniza quando há rótulo — nunca sobrescreve a seleção do usuário com ''.
  useEffect(() => {
    if (resolvedLabel) setInputText(resolvedLabel);
  }, [resolvedLabel]);

  return (
    <LookupAutocompleteView
      label={label}
      placeholder={placeholder}
      value={value}
      selectedLabel={inputText}
      options={options}
      isQuerySuccess={isQuerySuccess}
      search={search}
      hasError={hasError ?? false}
      onSearchChange={setSearch}
      onSelect={(id, itemLabel) => {
        onChange(id, itemLabel);
        setInputText(itemLabel);
        setSearch('');
      }}
      onClear={() => {
        onChange('', '');
        setInputText('');
        setSearch('');
      }}
    />
  );
}
